/**
 * Endpoints de **Logística → Documentos** (`/api/logistica/documentos`).
 *
 * El listado va paginado en el servidor (la tabla de ventas pasa de 100.000
 * comprobantes) y el cliente se busca por texto en lugar de cargar todos en un
 * desplegable.
 */

import { useQuery } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type { ClienteDocumento, DocumentosFiltros, DocumentosRespuesta } from './documentos.types';

export const documentosKeys = {
  lista: (filtros: DocumentosFiltros) => ['logistica', 'documentos', filtros] as const,
  clientes: (buscar: string) => ['logistica', 'documentos', 'clientes', buscar] as const,
} as const;

/** Arma la cadena de consulta del listado. */
function cadenaDe(filtros: DocumentosFiltros): string {
  const parametros = new URLSearchParams();

  if (filtros.desde) parametros.set('desde', filtros.desde);
  if (filtros.hasta) parametros.set('hasta', filtros.hasta);
  if (filtros.numero) parametros.set('numero', filtros.numero);
  if (filtros.cliente) parametros.set('cliente', filtros.cliente);
  if (filtros.search) parametros.set('search', filtros.search);
  if (filtros.page) parametros.set('page', String(filtros.page));
  if (filtros.perPage) parametros.set('per_page', String(filtros.perPage));

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

export const documentosApi = {
  listar: (filtros: DocumentosFiltros = {}, signal?: AbortSignal): Promise<DocumentosRespuesta> =>
    apiClient.get<DocumentosRespuesta>(`/api/logistica/documentos${cadenaDe(filtros)}`, { signal }),
  clientes: (buscar: string, signal?: AbortSignal): Promise<{ clientes: ClienteDocumento[] }> =>
    apiClient.get<{ clientes: ClienteDocumento[] }>(
      `/api/logistica/documentos/clientes?buscar=${encodeURIComponent(buscar)}`,
      { signal },
    ),
};

/** Comprobantes del filtro pedido. */
export function useDocumentos(filtros: DocumentosFiltros = {}) {
  return useQuery({
    queryKey: documentosKeys.lista(filtros),
    queryFn: ({ signal }) => documentosApi.listar(filtros, signal),
  });
}

/** Sugerencias de cliente para el filtro (a partir de dos letras). */
export function useBuscarClientes(buscar: string) {
  return useQuery({
    queryKey: documentosKeys.clientes(buscar),
    queryFn: ({ signal }) => documentosApi.clientes(buscar, signal),
    enabled: buscar.trim().length >= 2,
    staleTime: 5 * 60 * 1000,
  });
}
